'use client';

import { suggest, SuggestResponseItem } from '@yandex/ymaps3-types';
import { useTranslations } from 'next-intl';
import { Key, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';

import { UiKitAutocomplete } from '@/ui-kit/autocomplete';
import { BaseOption } from '@/ui-kit/listbox';
import { getAddressFromSuggest } from '@/util/map-point-maps/utils/getAddressFromSuggest';
import { highlightText } from '@/util/map-point-maps/utils/highlightText';

import { styles } from './UiKitSearchLocationField.styles';

const getSuggestKey = (result: SuggestResponseItem) =>
  result.value + result.uri + result.title.text + result.subtitle?.text + result.type;

export interface UiKitSearchLocationFieldProps {
  suggest: typeof suggest;
  onSelect: (option: SuggestResponseItem | null) => void;
  city?: string;
}

export function UiKitSearchLocationField({
  suggest,
  onSelect,
  city
}: UiKitSearchLocationFieldProps) {
  const slots = styles();

  const t = useTranslations();

  const [suggestResults, setSuggestResults] = useState<SuggestResponseItem[]>([]);

  const [currentValue, setCurrentValue] = useState<string>('');

  const handleSelect = (key: Key | null) => {
    const result = suggestResults.find((result) => getSuggestKey(result) === key);

    onSelect(result ?? null);
  };

  const debouncedSuggest = useDebouncedCallback(async (value: string) => {
    const results = await suggest({
      text: `${city ?? ''} ${value}`.trim()
    });

    const uniqueResultsMap = new Map<string, SuggestResponseItem>();
    results.forEach((result) => {
      const key = getSuggestKey(result);
      if (!uniqueResultsMap.has(key)) {
        uniqueResultsMap.set(key, result);
      }
    });

    setSuggestResults(Array.from(uniqueResultsMap.values()));
    setCurrentValue(value);
  }, 300);

  return (
    <div className={slots.base()}>
      <UiKitAutocomplete<SuggestResponseItem & BaseOption>
        options={suggestResults.map((result) => ({
          ...result,
          value: getSuggestKey(result),
          label: getAddressFromSuggest(result).trim()
        }))}
        onChange={debouncedSuggest}
        onSelect={handleSelect}
        placeholder={t('findAddress')}
        label={t('findAddress')}
        state="map"
        renderOption={(option) => (
          <span className={slots.option()}>
            {highlightText(option.label, currentValue).map((part, index) => (
              <span
                key={`${part.value}-${index}`}
                className={slots.text({ highlight: part.type === 'highlight' })}
              >
                {part.value}
              </span>
            ))}
          </span>
        )}
      />
    </div>
  );
}
